import { useQuery } from '@tanstack/react-query';

import {
  AdminDashboardOrgDetail,
  AdminDashboardOrganizationSummary,
  AdminDashboardSummary,
  AdminDashboardUserSummary,
  StoragePreference,
} from './types';
import { RawDriveListApiResponse } from '../../types';
import { BASE_ADMIN_DASHBOARD_API } from '../../utils/constants';
import { fetchWithAuthV2, fetchWithAuth } from '../../utils/requests';

const FIVE_MINUTES = 5 * 60 * 1000;
const FIFTEEN_MINUTES = 15 * 60 * 1000;

const DEFAULT_QUERY_OPTIONS = {
  gcTime: FIFTEEN_MINUTES,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

const STATIC_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: Infinity,
};

const SHORT_LIVED_QUERY_OPTIONS = {
  ...DEFAULT_QUERY_OPTIONS,
  staleTime: FIVE_MINUTES,
};

const createDateRangeBody = (startDate?: string, endDate?: string) => {
  const body: { start_date?: string; end_date?: string } = {};
  if (startDate) body.start_date = startDate;
  if (endDate) body.end_date = endDate;
  return body;
};

const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

const createDateRangeQueryParams = (
  startDate?: string,
  endDate?: string,
  additionalParams?: Record<string, string>
) => {
  const queryParams = new URLSearchParams();

  if (startDate) queryParams.set('start_date', startDate);
  if (endDate) queryParams.set('end_date', endDate);

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      queryParams.set(key, value);
    });
  }

  return queryParams;
};

const withQueryParams = (path: string, queryParams: URLSearchParams) => {
  const queryString = queryParams.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const toNumberRecord = (value: unknown): Record<string, number> => {
  if (!isRecord(value)) {
    return {};
  }

  const counts: Record<string, number> = {};
  Object.entries(value).forEach(([key, item]) => {
    if (typeof item === 'number') {
      counts[key] = item;
    }
  });

  return counts;
};

/**
 * Transform storage_preferences from array of objects to array of region strings
 * @param storagePreferences - Array of StoragePreference objects from API
 * @returns Array of region strings
 */
const transformStoragePreferences = (
  storagePreferences: StoragePreference[] | string[]
): string[] => {
  // If already array of strings, return as is
  if (
    Array.isArray(storagePreferences) &&
    storagePreferences.length > 0 &&
    typeof storagePreferences[0] === 'string'
  ) {
    return storagePreferences as string[];
  }

  // If array of objects, extract region field
  if (Array.isArray(storagePreferences)) {
    return storagePreferences
      .map((pref) => {
        if (typeof pref === 'object' && 'region' in pref) {
          return pref.region;
        }
        return null;
      })
      .filter((region): region is string => region !== null);
  }

  return [];
};

const fetchAdminDashboardOrganizationSummary = async (
  startDate?: string,
  endDate?: string
): Promise<AdminDashboardOrganizationSummary> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_organization_summary`;
  const body = {
    ...createDateRangeBody(startDate, endDate),
    export_csv: false,
  };

  const response = (await fetchWithAuthV2(path, {
    method: 'POST',
    fallbackErrorMessage: 'failed to fetch Organization Summary',
    body: JSON.stringify(body),
  })) as AdminDashboardOrganizationSummary;

  return response;
};

const downloadCsvFile = (
  csvContent: string,
  filename = 'active_users_export.csv'
) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const appendSuffixToFilename = (filename: string, suffix: string) => {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex <= 0) {
    return `${filename}${suffix}`;
  }

  const baseName = filename.slice(0, dotIndex);
  const extension = filename.slice(dotIndex);
  return `${baseName}${suffix}${extension}`;
};

const appendTimestampToFilename = (filename: string) => {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
    now.getDate()
  )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;

  return appendSuffixToFilename(filename, `_${timestamp}`);
};

const appendDateRangeToFilename = (
  filename: string,
  startDate?: string,
  endDate?: string
) => {
  if (!startDate || !endDate) {
    return filename;
  }

  return appendSuffixToFilename(filename, `_from_${startDate}_to_${endDate}`);
};

const getCsvFilenameFromHeader = (
  contentDisposition: string | null,
  startDate?: string,
  endDate?: string
) => {
  const fallbackFilename = 'dashboard_growth_trend_export.csv';
  const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/i);
  const filename = filenameMatch?.[1] || fallbackFilename;
  const filenameWithRange = appendDateRangeToFilename(
    filename,
    startDate,
    endDate
  );
  return appendTimestampToFilename(filenameWithRange);
};

const exportAdminDashboardOrganizationCsv = async (
  startDate?: string,
  endDate?: string
): Promise<void> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_organization_summary`;
  const body = {
    ...createDateRangeBody(startDate, endDate),
    export_csv: true,
  };

  const response = await fetchWithAuth(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('failed to export Organization CSV');
  }

  const csvText = await response.text();
  const filename = getCsvFilenameFromHeader(
    response.headers.get('Content-Disposition'),
    startDate,
    endDate
  );
  downloadCsvFile(csvText, filename);
};

const fetchAdminDashboardUserSummary = async (
  startDate?: string,
  endDate?: string,
  includeOuster = true
): Promise<AdminDashboardUserSummary> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_user_summary`;
  const body = {
    ...createDateRangeBody(startDate, endDate),
    export_csv: false,
    include_ouster: includeOuster,
  };

  const response = (await fetchWithAuthV2(path, {
    method: 'POST',
    fallbackErrorMessage: 'failed to fetch User Summary',
    body: JSON.stringify(body),
  })) as AdminDashboardUserSummary;

  return response;
};

const exportAdminDashboardUserCsv = async (
  startDate?: string,
  endDate?: string,
  includeOuster = true
): Promise<void> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_user_summary`;
  const body = {
    ...createDateRangeBody(startDate, endDate),
    export_csv: true,
    include_ouster: includeOuster,
  };

  const response = await fetchWithAuth(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error('failed to export User CSV');
  }

  const csvText = await response.text();
  const filename = getCsvFilenameFromHeader(
    response.headers.get('Content-Disposition'),
    startDate,
    endDate
  );
  downloadCsvFile(csvText, filename);
};

const fetchAdminDashboardSummary = async (): Promise<AdminDashboardSummary> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_summary_days/0`;
  const response = (await fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch Org User List',
  })) as AdminDashboardSummary;

  response.organizations = response.organizations
    .filter((org) => org.status !== null)
    .sort((a, b) => a.organization_id - b.organization_id)
    .map((org) => ({
      ...org,
      storage_preferences: transformStoragePreferences(org.storage_preferences),
    }));

  return response;
};

const fetchAdminDashboardSummaryByRange = async (
  range: number
): Promise<AdminDashboardSummary> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_summary_days/${range}`;
  const response = (await fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch Org User List',
  })) as AdminDashboardSummary;

  response.organizations = response.organizations.map((org) => ({
    ...org,
    storage_preferences: transformStoragePreferences(org.storage_preferences),
  }));

  return response;
};

const fetchAdminDashboardOrgDetail = async (
  organization_id: number,
  page: number,
  per_page: number
): Promise<AdminDashboardOrgDetail> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_users_by_org`;
  const response = (await fetchWithAuthV2(path, {
    method: 'POST',
    fallbackErrorMessage: 'failed to fetch Org User List',
    body: JSON.stringify({ 'user-org-id': organization_id, page, per_page }),
  })) as AdminDashboardOrgDetail;

  const users = response.users.filter((user) => user.email !== null);
  response.users = users;

  // Transform storage_preferences
  response.storage_preferences = transformStoragePreferences(
    response.storage_preferences
  );

  return response;
};

const fetchStaleRecordings = async (
  page = 1,
  per_page = 20,
  osf_status: 'NOT_STARTED' | 'FAILED' = 'NOT_STARTED'
): Promise<RawDriveListApiResponse> => {
  // Alternative path for stale drives endpoint (uncomment if needed for legacy support):
  const path = `${BASE_ADMIN_DASHBOARD_API}/stale_drives`;
  const queryParams = new URLSearchParams({
    page: page.toString(),
    per_page: per_page.toString(),
    osf_status,
  });

  const response = await fetchWithAuth(`${path}?${queryParams}`);
  const json = (await response.json()) as RawDriveListApiResponse;

  return json;
};

const fetchSupportedRegions = async (): Promise<string[]> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/supported_regions`;
  const response = (await fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch supported regions',
  })) as { regions: string[] };

  return response.regions;
};

const fetchActiveUserCounts = async (
  startDate?: string,
  endDate?: string,
  excludeOuster = false,
  granularity: 'day' | 'month' = 'day'
): Promise<Record<string, number>> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/active_user/counts`;
  const extra: Record<string, string> = {
    timezone: USER_TIMEZONE,
    granularity,
  };
  if (excludeOuster) extra.exclude_ouster = 'true';
  const queryParams = createDateRangeQueryParams(startDate, endDate, extra);
  const rawResponse: unknown = await fetchWithAuthV2(
    withQueryParams(path, queryParams),
    {
      method: 'GET',
      fallbackErrorMessage: 'failed to fetch active user counts',
    }
  );

  return toNumberRecord(rawResponse);
};

const fetchAndDownloadCsv = async (
  url: string,
  errorMessage: string,
  startDate?: string,
  endDate?: string
) => {
  const response = await fetchWithAuth(url);
  if (!response.ok) {
    throw new Error(errorMessage);
  }

  const csvText = await response.text();
  const filename = getCsvFilenameFromHeader(
    response.headers.get('Content-Disposition'),
    startDate,
    endDate
  );

  downloadCsvFile(csvText, filename);
  return csvText;
};

const fetchActiveUserDetails = async (
  startDate?: string,
  endDate?: string,
  format: 'json' | 'csv' = 'json'
): Promise<string> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/active_user/userdetails`;
  const queryParams = createDateRangeQueryParams(startDate, endDate, {
    format,
  });

  return fetchAndDownloadCsv(
    withQueryParams(path, queryParams),
    'failed to fetch active user details csv',
    startDate,
    endDate
  );
};

const fetchUploadCounts = async (
  startDate?: string,
  endDate?: string,
  excludeOuster = false
): Promise<Record<string, number>> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/upload/counts`;
  const extra: Record<string, string> = { timezone: USER_TIMEZONE };
  if (excludeOuster) extra.exclude_ouster = 'true';
  const queryParams = createDateRangeQueryParams(startDate, endDate, extra);
  const rawResponse = (await fetchWithAuthV2(
    withQueryParams(path, queryParams),
    {
      method: 'GET',
      fallbackErrorMessage: 'failed to fetch upload counts',
    }
  )) as unknown;

  return toNumberRecord(isRecord(rawResponse) ? rawResponse : undefined);
};

const fetchUploadDetails = async (
  startDate?: string,
  endDate?: string,
  excludeOuster = false
): Promise<string> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/upload/details`;
  const additionalParams: Record<string, string> = {
    format: 'csv',
    timezone: USER_TIMEZONE,
  };
  if (excludeOuster) additionalParams.exclude_ouster = 'true';
  const queryParams = createDateRangeQueryParams(
    startDate,
    endDate,
    additionalParams
  );

  return fetchAndDownloadCsv(
    withQueryParams(path, queryParams),
    'failed to fetch upload details csv',
    startDate,
    endDate
  );
};

const fetchUploadTotal = async (): Promise<{
  total_uploads: number;
}> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/upload/total`;
  return fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch total uploads',
  }) as Promise<{ total_uploads: number }>;
};

const fetchAdminDashboardOrganizationTotal = async (): Promise<{
  total_organizations: number;
}> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_organization_total`;

  return fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch organization total',
  }) as Promise<{ total_organizations: number }>;
};

const fetchAdminDashboardUserTotal = async (): Promise<{
  total_users: number;
}> => {
  const path = `${BASE_ADMIN_DASHBOARD_API}/get_studio_user_total`;

  return fetchWithAuthV2(path, {
    method: 'GET',
    fallbackErrorMessage: 'failed to fetch user total',
  }) as Promise<{ total_users: number }>;
};

export const useAdminDashboardOrganizationSummary = (
  startDate?: string,
  endDate?: string
) =>
  useQuery<AdminDashboardOrganizationSummary, Error>({
    queryKey: ['adminDashboardOrganizationSummary', startDate, endDate],
    queryFn: () => fetchAdminDashboardOrganizationSummary(startDate, endDate),
    ...STATIC_QUERY_OPTIONS,
  });

export const useAdminDashboardUserSummary = (
  startDate?: string,
  endDate?: string,
  includeOuster = true
) =>
  useQuery<AdminDashboardUserSummary, Error>({
    queryKey: ['adminDashboardUserSummary', startDate, endDate, includeOuster],
    queryFn: () =>
      fetchAdminDashboardUserSummary(startDate, endDate, includeOuster),
    ...STATIC_QUERY_OPTIONS,
  });

export const useExportUserCsv = (
  startDate?: string,
  endDate?: string,
  includeOuster = true
) =>
  useQuery<void, Error>({
    queryKey: ['exportUserCsv', startDate, endDate, includeOuster],
    queryFn: () =>
      exportAdminDashboardUserCsv(startDate, endDate, includeOuster),
    enabled: false,
  });

export const useAdminDashboardSummary = () =>
  useQuery<AdminDashboardSummary, Error>({
    queryKey: ['adminDashboardSummary'],
    queryFn: fetchAdminDashboardSummary,
    ...STATIC_QUERY_OPTIONS,
  });

export const useAdminDashboardSummaryByRange = (range: number) =>
  useQuery<AdminDashboardSummary, Error>({
    queryKey: ['adminDashboardSummaryByRange', range],
    queryFn: () => fetchAdminDashboardSummaryByRange(range),
    ...STATIC_QUERY_OPTIONS,
  });

export const useAdminDashboardOrgDetail = (
  organization_id: number,
  page: number,
  per_page: number
) =>
  useQuery<AdminDashboardOrgDetail, Error>({
    queryKey: ['adminDashboardOrgDetail', organization_id, page, per_page],
    queryFn: () =>
      fetchAdminDashboardOrgDetail(organization_id, page, per_page),
    ...SHORT_LIVED_QUERY_OPTIONS,
  });

export const useStaleRecordings = (
  page = 1,
  per_page = 20,
  osf_status: 'NOT_STARTED' | 'FAILED' = 'NOT_STARTED',
  enabled = true
) => {
  const {
    data: { data = [], total_count: totalCount } = {},
    isError,
    isRefetching,
    isLoading,
    isSuccess,
    refetch,
  } = useQuery<RawDriveListApiResponse, Error>({
    queryKey: ['staleRecordings', page, per_page, osf_status],
    queryFn: () => fetchStaleRecordings(page, per_page, osf_status),
    ...SHORT_LIVED_QUERY_OPTIONS,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchInterval: FIVE_MINUTES,
    enabled,
  });

  return {
    data,
    totalCount,
    isError,
    isRefetching,
    isLoading,
    isSuccess,
    refetch,
  };
};

export const useSupportedRegions = () =>
  useQuery<string[], Error>({
    queryKey: ['supportedRegions'],
    queryFn: () => fetchSupportedRegions(),
    ...STATIC_QUERY_OPTIONS,
  });

export const useActiveUserCounts = (
  startDate?: string,
  endDate?: string,
  excludeOuster = false,
  granularity: 'day' | 'month' = 'day'
) =>
  useQuery<Record<string, number>, Error>({
    queryKey: [
      'activeUserCounts',
      startDate,
      endDate,
      excludeOuster,
      granularity,
    ],
    queryFn: () =>
      fetchActiveUserCounts(startDate, endDate, excludeOuster, granularity),
    ...SHORT_LIVED_QUERY_OPTIONS,
  });

export const useExportOrganizationCsv = (
  startDate?: string,
  endDate?: string,
  enabled = false
) =>
  useQuery<void, Error>({
    queryKey: ['exportOrganizationCsv', startDate, endDate],
    queryFn: () => exportAdminDashboardOrganizationCsv(startDate, endDate),
    ...SHORT_LIVED_QUERY_OPTIONS,
    enabled,
  });

export const useActiveUserDetails = (
  startDate?: string,
  endDate?: string,
  enabled = false
) =>
  useQuery<string, Error>({
    queryKey: ['activeUserDetailsCsv', startDate, endDate],
    queryFn: () => fetchActiveUserDetails(startDate, endDate, 'csv'),
    ...SHORT_LIVED_QUERY_OPTIONS,
    enabled,
  });

export const useUploadCounts = (
  startDate?: string,
  endDate?: string,
  excludeOuster = false
) =>
  useQuery<Record<string, number>, Error>({
    queryKey: ['uploadCounts', startDate, endDate, excludeOuster],
    queryFn: () => fetchUploadCounts(startDate, endDate, excludeOuster),
    ...SHORT_LIVED_QUERY_OPTIONS,
  });

export const useUploadDetails = (
  startDate?: string,
  endDate?: string,
  excludeOuster = false,
  enabled = false
) =>
  useQuery<string, Error>({
    queryKey: ['uploadDetailsCsv', startDate, endDate, excludeOuster],
    queryFn: () => fetchUploadDetails(startDate, endDate, excludeOuster),
    ...SHORT_LIVED_QUERY_OPTIONS,
    enabled,
  });

export const useAllTimeUploadTotal = () =>
  useQuery<{ total_uploads: number }, Error>({
    queryKey: ['allTimeUploadTotal'],
    queryFn: () => fetchUploadTotal(),
    ...STATIC_QUERY_OPTIONS,
  });

export const useAdminDashboardOrganizationTotal = () =>
  useQuery<{ total_organizations: number }, Error>({
    queryKey: ['adminDashboardOrganizationTotal'],
    queryFn: () => fetchAdminDashboardOrganizationTotal(),
    ...STATIC_QUERY_OPTIONS,
  });

export const useAdminDashboardUserTotal = () =>
  useQuery<{ total_users: number }, Error>({
    queryKey: ['adminDashboardUserTotal'],
    queryFn: () => fetchAdminDashboardUserTotal(),
    ...STATIC_QUERY_OPTIONS,
  });
